import { Button, CircularProgress, Paper, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import FernieMicro from "~/components/fernie/FernieMicro";
import { anchorIsPresent, findVisibleAnchor, resolveCurrentAnchor } from "~/components/walkthrough/anchorResolution";
import assertEffectAnchorAbsent from "~/components/walkthrough/gateWiring";
import WalkthroughCoachmark from "~/components/walkthrough/WalkthroughCoachmark";
import { WALKTHROUGH_Z_INDEX } from "~/components/walkthrough/walkthroughLayering";
import useLearningMutations from "~/hooks/mutations/useLearningMutations";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import { useSnacks } from "~/providers/SnackProvider";
import { useLazyMyLearningPathQuery } from "~/services/endpoints/apiEndpoints";
import { advanceStep, completeTour, goBack, resetTour, skipTour } from "~/store/walkthroughSlice";
import { fernieColors } from "~/themes/fernieBrand";
import { getWalkthrough, matchesWalkthroughRoute, staysOnWalkthroughRoute } from "~/walkthroughs/registry";
import buildWalkthroughOutcomeVariables, { type EndedWalkthrough } from "~/walkthroughs/walkthroughOutcome";

const POLL_INTERVAL_MS = 250;
const RESOLVE_TIMEOUT_MS = 3000;

const WalkthroughController = (): React.JSX.Element | null => {
	const { t } = useTranslation("common");
	const dispatch = useAppDispatch();
	const { pathname } = useLocation();
	const [openSnack] = useSnacks();
	const openSnackRef = useRef(openSnack);
	openSnackRef.current = openSnack;

	const [fetchLearningPath] = useLazyMyLearningPathQuery();
	const { recordWalkthroughOutcome } = useLearningMutations();

	const activeTourId = useAppSelector((state) => state.walkthrough.activeTourId);
	const dismissible = useAppSelector((state) => state.walkthrough.dismissible);
	const conceptId = useAppSelector((state) => state.walkthrough.conceptId);
	const status = useAppSelector((state) => state.walkthrough.status);
	const stepIndex = useAppSelector((state) => state.walkthrough.stepIndex);
	const mutationSignal = useAppSelector((state) => state.walkthrough.mutationSignal);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [resolving, setResolving] = useState(false);
	const [isAnchorLost, setIsAnchorLost] = useState(false);
	const boundPathnameRef = useRef<string | null>(null);

	const definition = activeTourId ? getWalkthrough(activeTourId) : undefined;
	const step = definition?.steps[stepIndex];

	const recordEndedTour = async (ended: EndedWalkthrough): Promise<void> => {
		const paths = ended.conceptId ? [] : ((await fetchLearningPath(undefined, true)).data?.myLearningPath ?? []);
		const variables = buildWalkthroughOutcomeVariables(ended, paths);
		if (variables) await recordWalkthroughOutcome(variables);
	};

	useEffect(() => {
		if (status !== "running" || !definition) return;
		if (stepIndex >= definition.steps.length) dispatch(completeTour());
	}, [status, stepIndex, definition]);

	useEffect(() => {
		if (status !== "completed" && status !== "abandoned") return;
		if (status === "completed") {
			openSnackRef.current(t(definition?.completionMessageKey ?? "walkthrough.defaultCompletionMessage"), "success");
		}
		if (activeTourId) void recordEndedTour({ conceptId, status, walkthroughId: activeTourId });
		dispatch(resetTour());
	}, [status]);

	useEffect(() => {
		if (status !== "running" || !definition) {
			boundPathnameRef.current = null;
			return;
		}
		if (boundPathnameRef.current === null) {
			if (matchesWalkthroughRoute(pathname, definition.routePath) || staysOnWalkthroughRoute(pathname, definition))
				boundPathnameRef.current = pathname;
			else dispatch(skipTour());
			return;
		}
		if (pathname !== boundPathnameRef.current && !staysOnWalkthroughRoute(pathname, definition)) dispatch(skipTour());
	}, [pathname, status, definition]);

	useEffect(() => {
		if (status !== "running" || !dismissible) return;
		const closeOnEscape = (event: KeyboardEvent) => {
			if (event.key !== "Escape" || event.defaultPrevented) return;
			event.preventDefault();
			event.stopPropagation();
			dispatch(skipTour());
		};
		window.addEventListener("keydown", closeOnEscape);
		return () => window.removeEventListener("keydown", closeOnEscape);
	}, [status, dismissible]);

	useEffect(() => {
		if (status !== "running" || step?.gate.kind !== "mutationSuccess") return;
		if (mutationSignal && [step.gate.signalId].flat().includes(mutationSignal)) dispatch(advanceStep());
	}, [status, step, mutationSignal]);

	useEffect(() => {
		setAnchorEl(null);
		setResolving(false);
		setIsAnchorLost(false);
		if (status !== "running" || !step) return;

		let disposed = false;
		let resolved = false;
		let advanced = false;
		let resolvePoll: number | undefined;
		let failTimer: number | undefined;
		let gatePoll: number | undefined;
		let watchPoll: number | undefined;
		let currentElement: HTMLElement | null = null;
		let clickHandler: ((event: MouseEvent) => void) | undefined;

		const clearResolveTimers = () => {
			if (resolvePoll !== undefined) window.clearInterval(resolvePoll);
			if (failTimer !== undefined) window.clearTimeout(failTimer);
			resolvePoll = undefined;
			failTimer = undefined;
		};

		const advance = () => {
			if (disposed || !resolved || advanced) return;
			advanced = true;
			dispatch(advanceStep());
		};

		const advanceWhenAnchorAppears = (gateAnchorId: string, isArmed: () => boolean) => {
			if (isArmed() && anchorIsPresent(gateAnchorId)) {
				advance();
				return;
			}
			gatePoll = window.setInterval(() => {
				if (!disposed && isArmed() && anchorIsPresent(gateAnchorId)) advance();
			}, POLL_INTERVAL_MS);
		};

		const attachClickTargetGate = (effectAnchorId: string) => {
			assertEffectAnchorAbsent(step.id, effectAnchorId);
			let clicked = false;
			clickHandler = (event) => {
				if (resolveCurrentAnchor(step.anchorId, currentElement)?.contains(event.target as Node)) clicked = true;
			};
			document.addEventListener("click", clickHandler, true);
			advanceWhenAnchorAppears(effectAnchorId, () => clicked);
		};

		const attachGate = () => {
			if (step.gate.kind === "clickTarget") {
				attachClickTargetGate(step.gate.effectAnchorId);
				return;
			}
			if (step.gate.kind === "elementAppears") advanceWhenAnchorAppears(step.gate.anchorId, () => true);
		};

		const followAnchor = () => {
			const next = resolveCurrentAnchor(step.anchorId, currentElement);
			if (next === currentElement) return;
			currentElement = next;
			setAnchorEl(next);
			setIsAnchorLost(next === null);
		};

		const tryResolve = (): boolean => {
			const element = findVisibleAnchor(step.anchorId);
			if (!element) return false;
			resolved = true;
			clearResolveTimers();
			setResolving(false);
			currentElement = element;
			setAnchorEl(element);
			attachGate();
			watchPoll = window.setInterval(() => {
				if (!disposed) followAnchor();
			}, POLL_INTERVAL_MS);
			return true;
		};

		if (!tryResolve()) {
			setResolving(true);
			resolvePoll = window.setInterval(() => {
				if (!disposed) tryResolve();
			}, POLL_INTERVAL_MS);
			failTimer = window.setTimeout(() => {
				if (disposed || resolved) return;
				clearResolveTimers();
				setResolving(false);
				openSnackRef.current(t("walkthrough.stepNotFound"), "warning");
				dispatch(skipTour());
			}, RESOLVE_TIMEOUT_MS);
		}

		return () => {
			disposed = true;
			clearResolveTimers();
			if (gatePoll !== undefined) window.clearInterval(gatePoll);
			if (watchPoll !== undefined) window.clearInterval(watchPoll);
			if (clickHandler) document.removeEventListener("click", clickHandler, true);
		};
	}, [status, activeTourId, stepIndex]);

	if (status !== "running" || !step) return null;

	if (!anchorEl) {
		if (!resolving && !isAnchorLost) return null;
		return (
			<Paper
				elevation={8}
				sx={{
					alignItems: "center",
					border: `1px solid ${fernieColors.springSoft}`,
					borderRadius: "14px",
					bottom: 24,
					display: "flex",
					gap: 1.25,
					left: "50%",
					position: "fixed",
					px: 2,
					py: 1.25,
					transform: "translateX(-50%)",
					zIndex: WALKTHROUGH_Z_INDEX.card
				}}
			>
				<FernieMicro decorative size={22} />
				<CircularProgress size={16} sx={{ color: fernieColors.fern }} />
				<Typography sx={{ color: fernieColors.richSoil, fontSize: "0.82rem", fontWeight: 600 }}>
					{t("walkthrough.findingOnScreen")}
				</Typography>
				{isAnchorLost && (
					<Button
						onClick={() => dispatch(skipTour())}
						size="small"
						sx={{ color: fernieColors.dim, textTransform: "none" }}
					>
						{t("walkthrough.skip")}
					</Button>
				)}
			</Paper>
		);
	}

	return (
		<WalkthroughCoachmark
			anchorEl={anchorEl}
			dismissible={dismissible}
			onBack={() => dispatch(goBack())}
			onNext={() => dispatch(advanceStep())}
			onSkip={() => dispatch(skipTour())}
			step={step}
			stepCount={definition?.steps.length ?? 0}
			stepIndex={stepIndex}
		/>
	);
};

export default WalkthroughController;
